import React from 'react';
import { CheckCircle, Clock, TrendingUp } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface NegotiationStatusBarProps {
  totalClauses: number;
  agreedClauses: number;
  pendingProposals?: number;
  priceImpact?: number;
  userRole: 'buyer' | 'seller' | 'agent';
}

export const NegotiationStatusBar: React.FC<NegotiationStatusBarProps> = ({
  totalClauses,
  agreedClauses,
  pendingProposals = 0,
  priceImpact,
  userRole,
}) => {
  const agreementPercentage =
    totalClauses > 0 ? Math.round((agreedClauses / totalClauses) * 100) : 0;
  const remainingClauses = totalClauses - agreedClauses;

  return (
    <Card className="taste-panel taste-rise mb-6 border-0 shadow-none">
      <CardHeader>
        <CardTitle className="font-display text-lg font-semibold tracking-tight text-[var(--primary-navy)] flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[var(--stage-active)]" />
          Contract Negotiation Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--on-surface-variant)]">
              Agreement Progress
            </span>
            <span className="text-display-sm font-display font-semibold text-[var(--primary-navy)]">
              {agreementPercentage}%
            </span>
          </div>
          <Progress value={agreementPercentage} className="h-2" />
          <p className="mt-2 text-xs text-[var(--on-surface-variant)]">
            {agreedClauses} of {totalClauses} clauses agreed
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8">
          <div className="rounded-lg bg-[var(--surface-container-lowest)] px-4 py-4">
            <div className="mb-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--on-tertiary-fixed-variant)]" />
              <p className="text-sm text-[var(--on-surface-variant)]">Agreed</p>
            </div>
            <p className="font-display text-2xl font-semibold text-[var(--on-tertiary-fixed-variant)]">
              {agreedClauses}
            </p>
          </div>

          <div className="rounded-lg bg-[var(--surface-container-lowest)] px-4 py-4">
            <div className="mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--on-surface-variant)]" />
              <p className="text-sm text-[var(--on-surface-variant)]">
                Remaining
              </p>
            </div>
            <p className="font-display text-2xl font-semibold text-[var(--on-surface)]">
              {remainingClauses}
            </p>
          </div>

          {pendingProposals > 0 && (
            <div className="rounded-lg bg-[var(--surface-container-lowest)] px-4 py-4">
              <div className="mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-[var(--primary-navy)]" />
                <p className="text-sm text-[var(--on-surface-variant)]">
                  {userRole === 'buyer'
                    ? 'Awaiting Your Response'
                    : 'Awaiting Response'}
                </p>
              </div>
              <p className="font-display text-2xl font-semibold text-[var(--primary-navy)]">
                {pendingProposals}
              </p>
            </div>
          )}

          {priceImpact !== undefined && priceImpact !== 0 && (
            <div className="rounded-lg bg-[var(--surface-container-lowest)] px-4 py-4">
              <p className="mb-2 text-sm text-[var(--on-surface-variant)]">
                Net Price Impact
              </p>
              <p
                className={`font-display text-2xl font-semibold ${
                  priceImpact > 0
                    ? 'text-[var(--on-tertiary-fixed-variant)]'
                    : 'text-destructive'
                }`}
              >
                {priceImpact > 0 ? '+' : ''}$
                {Math.abs(priceImpact).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-[var(--on-surface-variant)]">
                {priceImpact > 0 ? 'Higher offer' : 'Lower offer'}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
